import React, { useState, useEffect, useCallback } from "react";
import { format } from "date-fns";

import MessageDialog from "components/__reusable/MessageDialog";
import SecondaryActionButton from "components/__reusable/SecondaryActionButton";
import PrimaryActionButton from "components/__reusable/PrimaryActionButton";
import ProfileAvatar from "components/__reusable/ProfileAvatar";
import Checkbox from "components/__reusable/Checkbox";
import MonthPickerInput from "components/__reusable/MonthInput";
import useAppSelector from "hooks/useAppSelector";
import useAppDispatch from "hooks/useAppDispatch";
import { setSuccessStatus } from "features/success/successSlice";
import { setErrorStatus } from "features/error/errorSlice";
import { updateGraduationDate } from "features/student/studentAction";
import type { Student } from "types/Student";
import { isStudentGraduated } from "utilities/StudentStatus";
import { toMonthEnd, toMonthStart } from "utilities/MonthBoundary";

interface GraduationMonthDialogProps {
  onYesClick: () => void;
  onNoClick: () => void;
}

interface StudentGraduationData {
  student: Student;
  isSelected: boolean;
  graduationDate: Date;
}

const GraduationMonthDialog: React.FC<GraduationMonthDialogProps> = ({
  onYesClick,
  onNoClick,
}) => {
  const { students } = useAppSelector((state) => state.student);
  const dispatch = useAppDispatch();

  const [studentData, setStudentData] = useState<StudentGraduationData[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Default graduation date (March of next year)
  const getDefaultGraduationDate = (): Date => {
    const now = new Date();
    const nextYear = now.getFullYear() + 1;
    return new Date(nextYear, 2, 1); // March 1st of next year
  };

  // Minimum graduation date (current month)
  const getMinGraduationDate = (): Date => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  };

  // Initialize student data
  useEffect(() => {
    if (students.length > 0) {
      const activeStudents = students.filter(
        (student) => !isStudentGraduated(student)
      );

      const initialData = activeStudents.map((student) => ({
        student,
        isSelected: false,
        graduationDate: student.graduation_date
          ? toMonthStart(new Date(student.graduation_date))
          : toMonthStart(getDefaultGraduationDate()),
      }));

      // Sort by earliest graduation month as per specification
      initialData.sort(
        (a, b) => a.graduationDate.getTime() - b.graduationDate.getTime()
      );

      setStudentData(initialData);
    } else {
      setStudentData([]);
    }
  }, [students]);

  // Check if any students are selected
  const hasSelectedStudents = studentData.some((data) => data.isSelected);
  const isAllSelected =
    studentData.length > 0 && studentData.every((data) => data.isSelected);

  // Handle student selection
  const handleStudentSelect = useCallback(
    (studentId: number, isSelected: boolean) => {
      setStudentData((prev) =>
        prev.map((data) =>
          data.student.id === studentId ? { ...data, isSelected } : data
        )
      );
    },
    []
  );

  const handleSelectAll = useCallback((isSelected: boolean) => {
    setStudentData((prev) =>
      prev.map((data) => ({
        ...data,
        isSelected,
      }))
    );
  }, []);

  // Handle graduation date change
  const handleGraduationDateChange = useCallback(
    (studentId: number, date: Date) => {
      setStudentData(
        (prev) =>
          prev.map((data) =>
            data.student.id === studentId
              ? { ...data, graduationDate: date }
              : data
          )
        // Remove automatic re-sorting to prevent UI jumping
        // Sorting will happen on dialog initialization only
      );
    },
    []
  );

  // Handle save
  const handleSave = async () => {
    const selectedStudents = studentData.filter((data) => data.isSelected);

    if (selectedStudents.length === 0) {
      return;
    }

    setIsLoading(true);
    try {
      // Update each student individually
      for (const data of selectedStudents) {
        const formattedDate = format(
          toMonthEnd(data.graduationDate),
          "yyyy-MM-dd"
        );
        await dispatch(
          updateGraduationDate({
            id: data.student.id,
            graduationDate: formattedDate,
          })
        );
      }

      dispatch(
        setSuccessStatus({
          title: "卒園月設定完了",
          message: "選択した園児の卒園月を設定しました。",
        })
      );

      onYesClick();
    } catch (error: any) {
      dispatch(
        setErrorStatus(
          error.response?.data?.message || "卒園月の設定に失敗しました"
        )
      );
    } finally {
      setIsLoading(false);
    }
  };

  const renderStudentName = (student: Student) => {
    if (student.first_name_kata && student.last_name_kata) {
      return `${student.last_name} ${student.first_name} (${student.last_name_kata} ${student.first_name_kata})`;
    }
    return `${student.last_name} ${student.first_name}`;
  };

  return (
    <MessageDialog>
      <div className="h-[75vh] flex flex-col space-y-3">
        {/* Title with pink background */}
        <div className="bg-[#EC6E7D] p-3 rounded-t-lg">
          <h3 className="text-xl font-bold text-center text-white">
            卒園月を設定する
          </h3>
        </div>

        {/* Warning message */}
        <div className="px-3">
          <p className="text-sm text-gray-600 whitespace-pre">
            {`選択した園児の卒園月を変更します。\n※処理の途中で画面を離れた場合、情報は保存されません。`}
          </p>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto relative">
          <div className="w-full h-full absolute">
            <table className="w-full relative">
              {/* Fixed header */}
              <thead className="sticky top-0 z-10">
                <tr>
                  <th className="w-[10%] p-2 bg-[#f7f7f7] border border-[#D9D9D9] text-center align-middle">
                    <div className="flex justify-center">
                      <Checkbox
                        name="select-all-students"
                        label="すべて選択"
                        labelClassName="sr-only"
                        isChecked={isAllSelected}
                        onChange={handleSelectAll}
                      />
                    </div>
                  </th>
                  <th className="w-[50%] p-2 bg-[#f7f7f7] border border-[#D9D9D9] text-center">
                    園児名
                  </th>
                  <th className="w-[40%] p-2 bg-[#f7f7f7] border border-[#D9D9D9] text-center">
                    卒園月
                  </th>
                </tr>
              </thead>
              <tbody>
                {studentData.map((data) => (
                  <tr key={data.student.id}>
                    <td className="w-[10%] p-2 border border-[#D9D9D9] text-center">
                      <Checkbox
                        name={`student-graduation-${data.student.id}`}
                        label=""
                        isChecked={data.isSelected}
                        onChange={(isSelected) =>
                          handleStudentSelect(data.student.id, isSelected)
                        }
                        aria-label={`${renderStudentName(data.student)}を選択`}
                      />
                    </td>
                    <td className="w-[50%] p-2 border border-[#D9D9D9]">
                      <div className="flex items-center space-x-3">
                        <ProfileAvatar
                          pictureUrl={
                            data.student.profile_picture_for_admin || ""
                          }
                        />
                        <span className="text-sm">
                          {renderStudentName(data.student)}
                        </span>
                      </div>
                    </td>
                    <td className="w-[40%] p-2 border border-[#D9D9D9]">
                      <div className="flex items-center space-x-2">
                        <MonthPickerInput
                          value={data.graduationDate}
                          onChange={(date) =>
                            handleGraduationDateChange(data.student.id, date)
                          }
                          disable={!data.isSelected}
                          minDate={getMinGraduationDate()}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {studentData.length === 0 && (
              <div className="h-full flex items-center justify-center">
                <p className="text-gray-500">データがありません</p>
              </div>
            )}
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex justify-between items-center space-x-5 pt-3">
          <div className="w-1/2">
            <SecondaryActionButton label="キャンセル" onClick={onNoClick} />
          </div>
          <div className="w-1/2">
            <PrimaryActionButton
              label="卒園月を保存する"
              loading={isLoading}
              disabled={!hasSelectedStudents}
              onClick={handleSave}
            />
          </div>
        </div>
      </div>
    </MessageDialog>
  );
};

export default GraduationMonthDialog;
